
import React from "react";
import { CheckCircle, Shield, Lightbulb, Compass } from "lucide-react";

const ConsultingBackground = () => {
  return (
    <section className="section-spacing bg-white">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="inline-block text-primary font-medium mb-4 px-3 py-1 bg-primary/10 rounded-full text-sm">
            Mein Mehrwert für Ihr Unternehmen
          </span>
          <h2 className="leading-tight mb-6">Diskrete und effektive Begleitung für nachhaltige Erfolge</h2>
          <p className="text-joerg-600 mx-auto text-balance mb-8">
            Als Executive Coach für die Metropolregion Nürnberg und Bayern konzentriere sich meine Arbeit auf die konkreten Bedürfnisse Ihrer Organisation 
            und vor allem die Menschen dahinter. Mit flexiblen Formaten (Präsenz bis 120km um Roth, Remote deutschlandweit) biete ich 
            durch ein tiefes Verständnis komplexer Dynamiken und langjährige Führungserfahrung maßgeschneiderte Unterstützungsleistungen.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left column - Executive Support */}
          <div className="bg-joerg-50 p-8 rounded-2xl shadow-elevation">
            <div className="mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                  <Shield className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-joerg-800">Vertrauliche Führungskräfte-Begleitung</h3>
              </div>
              <p className="text-joerg-600 mb-6">
                Besonders für Führungskräfte mit außergewöhnlicher Wahrnehmungsstärke biete ich einen geschützten Raum für 
                persönliche und berufliche Entwicklung – ohne direktes Reporting an die Geschäftsführung.
              </p>
              
              <div className="space-y-3 ml-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-joerg-700">Diskrete Begleitung bei herausfordernden Führungssituationen</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-joerg-700">Entwicklung individueller Führungskompetenzen ohne öffentliche Exposition</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-joerg-700">Unterstützung bei der Identifikation und Förderung von Führungspotential</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Organizational Development */}
          <div className="bg-joerg-50 p-8 rounded-2xl shadow-elevation">
            <div className="mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                  <Compass className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-joerg-800">Strategische Organisationsentwicklung</h3>
              </div>
              <p className="text-joerg-600 mb-6">
                Durch meine ausgeprägte Wahrnehmungsfähigkeit und ganzheitlichen Ansatz unterstütze ich Unternehmen dabei, 
                eine zukunftsfähige Organisationskultur zu entwickeln, die sowohl wirtschaftlichen Erfolg als auch 
                Mitarbeiterwohlbefinden in den Mittelpunkt stellt.
              </p>
              
              <div className="space-y-3 ml-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-joerg-700">Entwicklung krisenresistenter Führungsstrukturen</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-joerg-700">Steigerung der Mitarbeiterbindung durch echte Wertschätzung</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-joerg-700">Präzise Evaluation bei der Integration neuer Führungskräfte</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section - Quote and Innovation */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-1 bg-joerg-50 p-6 rounded-2xl border-l-4 border-primary">
            <p className="text-joerg-700 italic text-center">
              "Mein Ziel ist es, die menschliche Komponente in einer zunehmend technologisierten Arbeitswelt zu stärken – denn gerade in Zeiten der KI-Integration macht differenzierte Wahrnehmung den entscheidenden Unterschied."
            </p>
            <p className="text-joerg-500 text-xs mt-3 text-center">
              — Aus meiner Führungsphilosophie
            </p>
          </div>
          
          <div className="md:col-span-2">
            {/* Updated layout for better responsive design - flex column on mobile, row on desktop */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-5 md:gap-6">
              {/* Icon centered on mobile, aligned left on desktop */}
              <div className="flex-shrink-0 h-14 w-14 bg-primary/15 rounded-full flex items-center justify-center text-primary shadow-md transform transition-transform hover:scale-105 mb-4 md:mb-0">
                <Lightbulb className="h-7 w-7" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-joerg-800 text-center md:text-left">Innovation durch menschliche Tiefe</h3>
                <p className="text-joerg-600 text-center md:text-left">
                  Während viele Beratungsansätze sich auf Prozessoptimierung und Kennzahlen fokussieren, 
                  konzentriere ich mich auf die Entwicklung der menschlichen Potenziale Ihrer Organisation. 
                  Meine ausgeprägte Fähigkeit, Menschen und Situationen präzise zu erfassen, ermöglicht 
                  nachhaltige Verbesserungen, die Ihr Unternehmen in einer sich schnell wandelnden Welt 
                  widerstandsfähiger und anpassungsfähiger machen – stets mit klarer Abgrenzung zu 
                  medizinisch-psychologischen Interventionen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultingBackground;
